import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import { 
  Package, 
  CreditCard, 
  Building2, 
  Upload, 
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  HardDrive,
  Calendar
} from 'lucide-react';
import { getPlanByTier } from '../../config/subscriptionPlans';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase';

export default function SubscriptionCheckout() {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const [checkoutData, setCheckoutData] = useState<any>(null);
  const [paymentMethod, setPaymentMethod] = useState<'promptpay' | 'bank'>('promptpay');
  const [paymentProof, setPaymentProof] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    const data = localStorage.getItem('subscriptionCheckout');
    if (!data) {
      navigate('/subscription/renew');
      return;
    }

    setCheckoutData(JSON.parse(data));
  }, [user, navigate]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setPaymentProof(e.target.files[0]);
    }
  };

  const handleSubmitPayment = async () => {
    if (!paymentProof) {
      alert('กรุณาอัปโหลดหลักฐานการชำระเงิน');
      return;
    }

    setUploading(true);

    try {
      // Create payment notification - only include fields with values
      const paymentData: any = {
        userId: user?.uid,
        userEmail: user?.email,
        type: checkoutData.isQuickRenew ? 'subscription_renewal' : (checkoutData.urlAddons && checkoutData.urlAddons.length > 0 ? 'url_addon' : 'storage_addon'),
        amount: checkoutData.total,
        paymentMethod,
        status: 'pending',
        createdAt: serverTimestamp(),
        paymentProofName: paymentProof.name,
      };

      // Add optional fields only if they exist
      if (checkoutData.plan) paymentData.plan = checkoutData.plan;
      if (checkoutData.planName) paymentData.planName = checkoutData.planName;
      if (checkoutData.planPrice) paymentData.planPrice = checkoutData.planPrice;
      if (checkoutData.addons && checkoutData.addons.length > 0) paymentData.addons = checkoutData.addons;
      if (checkoutData.storageAddon) paymentData.storageAddon = checkoutData.storageAddon;
      if (checkoutData.urlAddons && checkoutData.urlAddons.length > 0) paymentData.urlAddons = checkoutData.urlAddons;
      if (checkoutData.isRenewal) paymentData.isRenewal = checkoutData.isRenewal;
      if (checkoutData.isQuickRenew) paymentData.isQuickRenew = checkoutData.isQuickRenew;
      if (checkoutData.isStorageOnly) paymentData.isStorageOnly = checkoutData.isStorageOnly;

      await addDoc(collection(db, 'paymentNotifications'), paymentData);

      // Create admin notification
      const notificationTitle = checkoutData.isQuickRenew 
        ? `ต่ออายุ ${checkoutData.planName}`
        : checkoutData.storageAddon 
        ? 'ซื้อ Storage Add-on'
        : 'แจ้งชำระเงิน';

      const notificationMessage = checkoutData.isQuickRenew
        ? `${user?.email} แจ้งชำระเงินต่ออายุ ${checkoutData.planName}${checkoutData.addons && checkoutData.addons.length > 0 ? ` พร้อม Add-ons ${checkoutData.addons.length} รายการ` : ''}`
        : checkoutData.storageAddon
        ? `${user?.email} แจ้งชำระเงินซื้อ Storage +${checkoutData.storageAddon}GB`
        : `${user?.email} แจ้งชำระเงิน`;

      await addDoc(collection(db, 'admin_notifications'), {
        type: checkoutData.isQuickRenew ? 'subscription_renewal' : 'subscription_payment',
        title: notificationTitle,
        message: notificationMessage,
        userId: user?.uid,
        userEmail: user?.email,
        amount: checkoutData.total,
        read: false,
        createdAt: serverTimestamp(),
      });

      setShowSuccess(true);
      localStorage.removeItem('subscriptionCheckout');

      setTimeout(() => {
        navigate('/subscription/renew');
      }, 3000);
    } catch (error) {
      console.error('Error submitting payment:', error);
      alert('เกิดข้อผิดพลาดในการส่งข้อมูล กรุณาลองใหม่อีกครั้ง');
    } finally {
      setUploading(false);
    }
  };

  if (!checkoutData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  const selectedPlan = checkoutData.plan ? getPlanByTier(checkoutData.plan) : null;
  
  // Calculate storage from plan + storage addon
  const planStorageGB = selectedPlan ? (selectedPlan.features.storageGB.videoStorage / 1024) : 0;
  const storageAddonGB = checkoutData.storageAddon || 0;
  const totalStorage = planStorageGB + storageAddonGB;

  if (showSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">แจ้งชำระเงินสำเร็จ!</h2>
          <p className="text-gray-600 mb-6">
            เราได้รับการแจ้งชำระเงินของคุณแล้ว<br />
            ทีมงานจะตรวจสอบภายใน 24 ชั่วโมง
          </p>
          <p className="text-sm text-gray-500">
            คุณจะได้รับอีเมลยืนยันเมื่อการชำระเงินได้รับการอนุมัติ
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <button
          onClick={() => navigate('/subscription/renew')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          กลับ
        </button>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <CreditCard className="w-8 h-8 text-cyan-600" />
            สรุปการชำระเงิน
          </h1>
          <p className="text-gray-600 mt-2">ตรวจสอบรายการและชำระเงิน</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Column - Order Summary */}
          <div className="lg:col-span-2 space-y-6">
            {/* Order Details */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Package className="w-5 h-5 text-cyan-600" />
                {checkoutData.isQuickRenew ? 'ต่ออายุแพ็คเก็จ' : 'รายการที่เลือก'}
              </h2>

              {/* Plan Details - Show for all cases */}
              {(selectedPlan || checkoutData.planName) && (
                <div className="space-y-4 mb-4">
                  {/* Current Plan */}
                  <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl p-4 border-2 border-cyan-200">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="text-sm text-gray-600">แพ็คเก็จ</p>
                        <h3 className="text-xl font-bold text-cyan-600">{checkoutData.planName || selectedPlan?.name}</h3>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-cyan-600">
                          ฿{(checkoutData.planPrice || selectedPlan?.price || 0).toLocaleString()}
                        </p>
                        <p className="text-xs text-gray-500">
                          {checkoutData.plan?.includes('yearly') ? 'ต่อปี' : checkoutData.plan?.includes('semi_annual') ? '6 เดือน' : 'ต่อเดือน'}
                        </p>
                      </div>
                    </div>
                    {checkoutData.expiryDate && (
                      <div className="flex items-center gap-2 text-sm text-gray-600 bg-white rounded-lg p-2">
                        <Calendar className="w-4 h-4" />
                        <span>หมดอายุ: {checkoutData.expiryDate}</span>
                      </div>
                    )}
                  </div>

                  {/* Current Add-ons */}
                  {checkoutData.addons && checkoutData.addons.length > 0 && (
                    <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-4 border-2 border-purple-200">
                      <p className="text-sm font-semibold text-gray-700 mb-3">Add-ons ที่ต่ออายุด้วย</p>
                      <div className="space-y-2">
                        {checkoutData.addons.map((addon: any, idx: number) => (
                          <div key={idx} className="flex items-center justify-between bg-white rounded-lg p-2">
                            <div className="flex items-center gap-2">
                              <HardDrive className="w-4 h-4 text-purple-600" />
                              <span className="text-sm font-medium text-gray-900">{addon.name}</span>
                            </div>
                            <span className="text-sm font-semibold text-purple-600">+{addon.storageGB}GB</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* URL Add-ons */}
              {checkoutData.urlAddons && checkoutData.urlAddons.length > 0 && (
                <div className="space-y-3 mb-4">
                  {checkoutData.urlAddons.map((addon: any, idx: number) => {
                    const isPath = addon.type === 'path';
                    const price = isPath ? 49 : 99;
                    const url = isPath ? `edumi.co/${addon.username}` : `${addon.username}.edumi.co`;
                    
                    return (
                      <div key={idx} className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-4 border-2 border-purple-200">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-sm text-gray-600">{isPath ? 'Path URL' : 'Subdomain'}</p>
                            <h3 className="text-lg font-bold text-purple-600">{url}</h3>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-purple-600">
                              ฿{price.toLocaleString()}
                            </p>
                            <p className="text-xs text-gray-500">ต่อเดือน</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Storage Add-on */}
              {checkoutData.storageAddon && checkoutData.storageAddon > 0 && (
                <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-4 mb-4 border-2 border-orange-200">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="text-sm text-gray-600">Storage Add-on</p>
                      <h3 className="text-lg font-bold text-gray-900">+{checkoutData.storageAddon} GB</h3>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-orange-600">
                        ฿{(() => {
                          const gb = checkoutData.storageAddon;
                          if (gb === 5) return 99;
                          const rawPrice = (gb / 5) * 99;
                          return Math.floor(rawPrice / 10) * 10;
                        })().toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-500">ครั้งเดียว</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Total Storage Summary */}
              <div className="bg-blue-50 rounded-lg p-4 mt-4">
                <div className="flex items-center gap-2 mb-3">
                  <HardDrive className="w-5 h-5 text-blue-600" />
                  <span className="font-semibold text-gray-900">พื้นที่รวม</span>
                </div>
                <p className="text-3xl font-bold text-blue-600 mb-3">
                  {totalStorage.toFixed(0)} GB
                </p>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">จากแพ็คเก็จ:</span>
                    <span className="font-semibold text-gray-900">{planStorageGB.toFixed(0)} GB</span>
                  </div>
                  {storageAddonGB > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">จาก Add-on Storage:</span>
                      <span className="font-semibold text-orange-600">+{storageAddonGB} GB</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Payment Method */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-cyan-600" />
                วิธีการชำระเงิน
              </h2>

              <div className="space-y-3 mb-6">
                <button
                  onClick={() => setPaymentMethod('promptpay')}
                  className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                    paymentMethod === 'promptpay'
                      ? 'border-cyan-500 bg-cyan-50'
                      : 'border-gray-200 hover:border-cyan-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      paymentMethod === 'promptpay' ? 'border-cyan-500' : 'border-gray-300'
                    }`}>
                      {paymentMethod === 'promptpay' && (
                        <div className="w-3 h-3 rounded-full bg-cyan-500"></div>
                      )}
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">PromptPay</p>
                      <p className="text-sm text-gray-600">โอนผ่าน QR Code</p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => setPaymentMethod('bank')}
                  className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                    paymentMethod === 'bank'
                      ? 'border-cyan-500 bg-cyan-50'
                      : 'border-gray-200 hover:border-cyan-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      paymentMethod === 'bank' ? 'border-cyan-500' : 'border-gray-300'
                    }`}>
                      {paymentMethod === 'bank' && (
                        <div className="w-3 h-3 rounded-full bg-cyan-500"></div>
                      )}
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">โอนเงินผ่านธนาคาร</p>
                      <p className="text-sm text-gray-600">โอนเข้าบัญชีธนาคาร</p>
                    </div>
                  </div>
                </button>
              </div>

              {/* Payment Details */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 mb-4">
                <div className="flex items-center gap-2 mb-3">
                  <Building2 className="w-5 h-5 text-gray-600" />
                  <h3 className="font-bold text-gray-900">ข้อมูลการโอนเงิน</h3>
                </div>
                {paymentMethod === 'promptpay' ? (
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">PromptPay ID:</span>
                      <span className="font-semibold text-gray-900">0XX-XXX-XXXX</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">ชื่อบัญชี:</span>
                      <span className="font-semibold text-gray-900">EduMi Platform</span>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">ธนาคาร:</span>
                      <span className="font-semibold text-gray-900">ธนาคารกสิกรไทย</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">เลขที่บัญชี:</span>
                      <span className="font-semibold text-gray-900">XXX-X-XXXXX-X</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">ชื่อบัญชี:</span>
                      <span className="font-semibold text-gray-900">EduMi Platform</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Upload Payment Proof */}
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center">
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-sm font-semibold text-gray-700 mb-2">
                  อัปโหลดหลักฐานการชำระเงิน
                </p>
                <p className="text-xs text-gray-500 mb-4">
                  รองรับไฟล์ JPG, PNG (ขนาดไม่เกิน 5MB)
                </p>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                  id="payment-proof"
                />
                <label
                  htmlFor="payment-proof"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg font-semibold hover:bg-cyan-700 cursor-pointer transition-all"
                >
                  <Upload className="w-4 h-4" />
                  เลือกไฟล์
                </label>
                {paymentProof && (
                  <p className="text-sm text-green-600 mt-3 flex items-center justify-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    {paymentProof.name}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Summary & Submit */}
          <div className="lg:col-span-1">
            <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl shadow-lg p-6 border-2 border-cyan-200 sticky top-4">
              <h2 className="text-xl font-bold text-gray-900 mb-4">สรุปยอดชำระ</h2>

              {/* Package Details */}
              {selectedPlan && (
                <div className="bg-white rounded-xl p-4 mb-4 border border-cyan-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Package className="w-5 h-5 text-cyan-600" />
                    <h3 className="font-bold text-gray-900">แพ็คเก็จที่เลือก</h3>
                  </div>
                  <div className="mb-3">
                    <p className="text-lg font-bold text-cyan-600">{checkoutData.planName || selectedPlan.name}</p>
                    <p className="text-sm text-gray-600">
                      {checkoutData.plan?.includes('yearly') ? 'รายปี' : checkoutData.plan?.includes('semi_annual') ? 'ราย 6 เดือน' : 'รายเดือน'}
                    </p>
                  </div>
                  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg p-3 mb-3">
                    <p className="text-xs text-gray-600 mb-2 font-semibold">คุณจะได้รับ:</p>
                    <ul className="space-y-1.5 text-xs text-gray-700">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                        <span>พื้นที่จัดเก็บ {planStorageGB.toFixed(0)} GB</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                        <span>สร้างคอร์สไม่จำกัด</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                        <span>นักเรียนไม่จำกัด</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                        <span>อัปโหลดความเร็วสูง</span>
                      </li>
                      {selectedPlan.features.salePageBuilder && (
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                          <span>สร้างหน้าขายสินค้า</span>
                        </li>
                      )}
                      {selectedPlan.features.seoOptimization && (
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                          <span>SEO Optimization</span>
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              )}

              {/* Pricing Breakdown */}
              <div className="space-y-3 mb-6">
                {(checkoutData.planName || checkoutData.planPrice) && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{checkoutData.planName || selectedPlan?.name}</span>
                    <span className="font-semibold text-cyan-600">
                      ฿{(checkoutData.planPrice || selectedPlan?.price || 0).toLocaleString()}
                    </span>
                  </div>
                )}
                {checkoutData.isQuickRenew && checkoutData.addons && checkoutData.addons.length > 0 && (
                  <div className="space-y-2">
                    {checkoutData.addons.map((addon: any, idx: number) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span className="text-gray-600">{addon.name}</span>
                        <span className="font-semibold text-purple-600">
                          +{addon.storageGB}GB
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {checkoutData.urlAddons && checkoutData.urlAddons.length > 0 && (
                  <div className="space-y-2">
                    {checkoutData.urlAddons.map((addon: any, idx: number) => {
                      const isPath = addon.type === 'path';
                      const price = isPath ? 49 : 99;
                      return (
                        <div key={idx} className="flex justify-between text-sm">
                          <span className="text-gray-600">{isPath ? 'Path URL' : 'Subdomain'}</span>
                          <span className="font-semibold text-purple-600">
                            ฿{price.toLocaleString()}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
                {checkoutData.storageAddon && checkoutData.storageAddon > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Storage +{checkoutData.storageAddon}GB</span>
                    <span className="font-semibold text-orange-600">
                      ฿{(() => {
                        const gb = checkoutData.storageAddon;
                        if (gb === 5) return 99;
                        const rawPrice = (gb / 5) * 99;
                        return Math.floor(rawPrice / 10) * 10;
                      })().toLocaleString()}
                    </span>
                  </div>
                )}
                <div className="border-t border-cyan-200 pt-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-lg font-bold text-gray-900">ยอดรวมทั้งหมด</span>
                    <span className="text-3xl font-bold text-cyan-600">
                      ฿{checkoutData.total.toLocaleString()}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 text-right">
                    {checkoutData.isQuickRenew 
                      ? (checkoutData.plan?.includes('yearly') ? 'ต่อปี' : checkoutData.plan?.includes('semi_annual') ? '6 เดือน' : 'ต่อเดือน')
                      : (checkoutData.urlAddons && checkoutData.urlAddons.length > 0 ? 'ต่อเดือน' : 'ครั้งเดียว')
                    }
                  </p>
                  {/* Total Storage Summary */}
                  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg p-3 mt-3">
                    <div className="flex items-center gap-2 mb-2">
                      <HardDrive className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-semibold text-gray-900">พื้นที่รวม</span>
                    </div>
                    <p className="text-2xl font-bold text-blue-600 mb-2">
                      {totalStorage.toFixed(0)} GB
                    </p>
                    <div className="space-y-1 text-xs">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">จากแพ็คเก็จ:</span>
                        <span className="font-semibold text-gray-900">{planStorageGB.toFixed(0)} GB</span>
                      </div>
                      {storageAddonGB > 0 && (
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">จาก Add-on:</span>
                          <span className="font-semibold text-orange-600">+{storageAddonGB} GB</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Important Notes */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-amber-800">
                    <p className="font-semibold mb-1">ข้อควรทราบ:</p>
                    <ul className="list-disc list-inside space-y-1 text-xs">
                      <li>โอนเงินตามยอดที่ระบุ</li>
                      <li>อัปโหลดหลักฐานการโอนเงิน</li>
                      <li>รอการตรวจสอบภายใน 24 ชม.</li>
                      <li>จะได้รับอีเมลยืนยัน</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <button
                onClick={handleSubmitPayment}
                disabled={!paymentProof || uploading}
                className={`w-full flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-bold text-lg shadow-lg transition-all ${
                  !paymentProof || uploading
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white hover:shadow-xl'
                }`}
              >
                {uploading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    กำลังส่งข้อมูล...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    แจ้งชำระเงิน
                  </>
                )}
              </button>

              <p className="text-xs text-gray-500 text-center mt-4">
                กดปุ่มเพื่อแจ้งชำระเงินและรอการตรวจสอบ
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
